import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import { codeGroupUpdateSchema } from '../schema/codeSchema';
import type { CodeGroupUpdateFormValues } from '../schema/codeSchema';
import type { CommonCodeGroupResponse } from '../types/code';

interface CodeGroupEditDialogProps {
  open: boolean;
  group: CommonCodeGroupResponse | null;
  onClose: () => void;
  onSubmit: (values: CodeGroupUpdateFormValues) => void;
  isLoading?: boolean;
  errorMessage?: string;
}

export function CodeGroupEditDialog({
  open,
  group,
  onClose,
  onSubmit,
  isLoading = false,
  errorMessage,
}: CodeGroupEditDialogProps) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CodeGroupUpdateFormValues>({
    resolver: zodResolver(codeGroupUpdateSchema),
  });

  useEffect(() => {
    if (open && group) {
      reset({
        groupName: group.groupName,
        description: group.description ?? '',
      });
    } else if (!open) {
      reset({});
    }
  }, [open, group, reset]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const handleFormSubmit = handleSubmit((values) => {
    onSubmit(values);
  });

  const fieldStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
    marginBottom: 'var(--spacing-3)',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
  };

  const inputStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };

  const readonlyStyle: React.CSSProperties = {
    ...inputStyle,
    opacity: 0.6,
    cursor: 'default',
    background: 'var(--color-surface-muted)',
  };

  const errorStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-danger)',
    marginTop: 'var(--spacing-1)',
  };

  return (
    <FormDialog
      open={open}
      title={t('commonCode.editGroupDialog.title')}
      onClose={onClose}
      onSubmit={() => void handleFormSubmit()}
      submitLabel={isLoading ? t('commonCode.editGroupDialog.submitting') : t('commonCode.editGroupDialog.submit')}
      cancelLabel={t('commonCode.editGroupDialog.cancel')}
    >
      <div>
        {/* 오류 배너 */}
        {errorMessage && (
          <div
            role="alert"
            style={{
              marginBottom: 'var(--spacing-3)',
              padding: 'var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
            }}
          >
            {errorMessage}
          </div>
        )}

        {/* 그룹 코드 — readonly */}
        <div style={fieldStyle}>
          <label htmlFor="edit-group-code" style={labelStyle}>
            {t('commonCode.createGroupDialog.groupCode.label')}
          </label>
          <input
            id="edit-group-code"
            type="text"
            value={group?.groupCode ?? ''}
            readOnly
            style={{ ...readonlyStyle, fontFamily: 'var(--font-family-mono)' }}
            aria-label={t('commonCode.editGroupDialog.codeReadonlyHint')}
          />
          <span
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--spacing-1)' }}
          >
            {t('commonCode.editGroupDialog.codeReadonlyHint')}
          </span>
        </div>

        {/* 그룹 이름 */}
        <div style={fieldStyle}>
          <label htmlFor="edit-group-name" style={labelStyle}>
            {t('commonCode.createGroupDialog.groupName.label')}
          </label>
          <input
            id="edit-group-name"
            type="text"
            placeholder={t('commonCode.createGroupDialog.groupName.placeholder')}
            {...register('groupName')}
            aria-invalid={!!errors.groupName}
            aria-describedby={errors.groupName ? 'edit-groupName-error' : undefined}
            style={{
              ...inputStyle,
              borderColor: errors.groupName ? 'var(--color-danger)' : 'var(--color-surface-border)',
            }}
          />
          {errors.groupName && (
            <span id="edit-groupName-error" style={errorStyle} role="alert">
              {errors.groupName.message}
            </span>
          )}
        </div>

        {/* 설명 */}
        <div style={fieldStyle}>
          <label htmlFor="edit-group-description" style={labelStyle}>
            {t('commonCode.createGroupDialog.description.label')}
          </label>
          <textarea
            id="edit-group-description"
            placeholder={t('commonCode.createGroupDialog.description.placeholder')}
            rows={2}
            {...register('description')}
            style={{
              ...inputStyle,
              resize: 'vertical',
              minHeight: 60,
            }}
          />
        </div>
      </div>
    </FormDialog>
  );
}
